import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';

/**
 * [PURPOSE]: Beautiful, consistent tooltip system for the entire app
 * [WHY]: Users need contextual help without cluttering the UI
 * [INTEGRATION]: Wrap any element to add a tooltip on hover
 *
 * Design Philosophy:
 * - **Subtle**: 300ms delay before showing (no flash on quick hovers)
 * - **Beautiful**: Dark background, subtle glow, smooth fade-in
 * - **Smart**: Auto-positions to avoid screen edges
 * - **Accessible**: Works with keyboard focus, aria-describedby
 * - **Flexible**: Supports multi-line text, custom positioning
 *
 * Usage:
 * <Tooltip content="This is a helpful tooltip">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * Advanced:
 * <Tooltip
 *   content="Complex tooltip with details"
 *   position="top"
 *   delay={500}
 *   maxWidth="300px"
 * >
 *   <div>Target element</div>
 * </Tooltip>
 */

const Tooltip = ({
  children,
  content,
  position = 'auto', // 'top' | 'bottom' | 'left' | 'right' | 'auto'
  delay = 300, // milliseconds before showing
  maxWidth = '250px',
  className = '',
  showIcon = false // Show info icon next to content
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [computedPosition, setComputedPosition] = useState(position);
  const timeoutRef = useRef(null);
  const targetRef = useRef(null);
  const tooltipRef = useRef(null);

  // [HANDLER]: Show tooltip after delay
  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);

      // [SMART POSITIONING]: Calculate best position if auto
      if (position === 'auto' && targetRef.current && tooltipRef.current) {
        const targetRect = targetRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        // Check space around target
        const spaceAbove = targetRect.top;
        const spaceBelow = window.innerHeight - targetRect.bottom;
        const spaceLeft = targetRect.left;
        const spaceRight = window.innerWidth - targetRect.right;

        // Choose position with most space
        if (spaceAbove > tooltipRect.height + 10 && spaceAbove > spaceBelow) {
          setComputedPosition('top');
        } else if (spaceBelow > tooltipRect.height + 10) {
          setComputedPosition('bottom');
        } else if (spaceRight > tooltipRect.width + 10) {
          setComputedPosition('right');
        } else if (spaceLeft > tooltipRect.width + 10) {
          setComputedPosition('left');
        } else {
          setComputedPosition('top'); // Default fallback
        }
      }
    }, delay);
  };

  // [HANDLER]: Hide tooltip immediately
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  // [CLEANUP]: Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // [HELPER]: Get position classes for tooltip
  const getPositionClasses = () => {
    const pos = position === 'auto' ? computedPosition : position;

    switch (pos) {
      case 'top':
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
    }
  };

  // [HELPER]: Get arrow position classes
  const getArrowClasses = () => {
    const pos = position === 'auto' ? computedPosition : position;

    switch (pos) {
      case 'top':
        return 'top-full left-1/2 -translate-x-1/2 -mt-1 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900';
      case 'bottom':
        return 'bottom-full left-1/2 -translate-x-1/2 -mb-1 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900';
      case 'left':
        return 'left-full top-1/2 -translate-y-1/2 -ml-1 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900';
      case 'right':
        return 'right-full top-1/2 -translate-y-1/2 -mr-1 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900';
      default:
        return 'top-full left-1/2 -translate-x-1/2 -mt-1 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900';
    }
  };

  // [EDGE CASE]: Don't render if no content
  if (!content) {
    return <>{children}</>;
  }

  return (
    <div
      ref={targetRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {children}

      {/* [TOOLTIP]: The actual tooltip element */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`
            absolute z-[9999] pointer-events-none
            ${getPositionClasses()}
          `}
          role="tooltip"
          aria-live="polite"
          style={{
            animation: 'fadeIn 0.15s ease-out'
          }}
        >
          {/* Tooltip Content Box */}
          <div
            className="bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-2xl border border-gray-700 whitespace-normal"
            style={{
              maxWidth,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)'
            }}
          >
            {showIcon && (
              <div className="flex items-start gap-2">
                <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div>{content}</div>
              </div>
            )}
            {!showIcon && content}
          </div>

          {/* Arrow pointer */}
          <div
            className={`absolute w-0 h-0 border-4 ${getArrowClasses()}`}
            style={{
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
            }}
          />
        </div>
      )}

      {/* [INLINE STYLES]: Fade-in animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default Tooltip;
